import { useEffect, useMemo, useRef } from "react";
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
} from "@tanstack/react-query";
import { useReadyAuth } from "@/auth/use-auth";
import { useContactsAdapter } from "./use-adapter";
import { applyPatchToContact, loggedContact } from "./derive";
import { insertContactPages, mergeContactPages, removeContactPages } from "./cache-updates";
import type {
  Contact,
  ContactInput,
  ContactLogMethod,
  ContactPatch,
  ContactScope,
  ContactsPage,
  ImportOptions,
  ImportPreview,
} from "./types";

/**
 * React Query bindings for the contacts surface. Reads are keyed by scope +
 * viewer. Every single-contact mutation (create/update/delete/log) applies its
 * change directly to the cache (insert / merge / remove) and only marks the roster
 * stale (deferred revalidation), so the visible change never waits on a full
 * refetch. Only bulk import actively refetches. Mirrors the signals/recipient
 * hook modules.
 */

const KEY = "contacts";

/** Export can hang on a slow/failed Edge Function; cap it so the button never
 *  sticks on "Exporting…" - a timeout surfaces as an error the UI can retry. */
const EXPORT_TIMEOUT_MS = 20_000;
function withTimeout<T>(p: Promise<T>, ms: number, message: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(() => {
      reject(new Error(message));
    }, ms);

    p.then(
      (value) => {
        window.clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        window.clearTimeout(timer);
        reject(err);
      }
    );
  });
}

/** Keep the first response small so the screen appears quickly, then use larger
 *  background pages to finish a large roster with far fewer round trips. Pages
 *  still load one at a time, so this never fans out a burst of heavy calls. */
const CONTACTS_INITIAL_PAGE_SIZE = 200;
const CONTACTS_BACKGROUND_PAGE_SIZE = 500;

interface ContactsPageParam {
  offset: number;
  limit: number;
}

export interface ContactsResult {
  /** Contacts loaded so far, deduped by id. Grows as pages arrive. */
  rows: Contact[];
  /** Exact roster total from the endpoint, known as soon as page 1 lands. */
  total: number | null;
  /** Every page has loaded and there is no outstanding page failure. */
  allLoaded: boolean;
  /** Page 1 has not landed yet (nothing to show). */
  initialLoading: boolean;
  /** Page 1 itself failed after its retry (nothing loaded). */
  initialError: boolean;
  /** Earlier pages loaded but a later page failed after its retry. */
  partialFailure: boolean;
  /** Retry the outstanding failure (page 1 refetch, or resume the next page). */
  retry: () => void;
}

/**
 * Progressive, resilient contacts read. The endpoint is paged one page at a
 * time (never an all-or-nothing fan-out): page 1 renders immediately with the
 * true total, later pages hydrate the list and tiles as they land, and a page
 * that fails after one retry stops the sweep so the UI can show what loaded
 * plus a manual retry rather than a blank page.
 */
export function useContacts(scope: ContactScope, opts?: { autoLoadAll?: boolean }): ContactsResult {
  const autoLoadAll = opts?.autoLoadAll ?? true;
  const adapter = useContactsAdapter();
  const { profile } = useReadyAuth();

  const q = useInfiniteQuery({
    queryKey: [KEY, scope, profile.id],
    initialPageParam: {
      offset: 0,
      limit: CONTACTS_INITIAL_PAGE_SIZE,
    } satisfies ContactsPageParam,
    queryFn: ({ pageParam }) =>
      adapter.listContactsPage(scope, profile.id, {
        limit: pageParam.limit,
        offset: pageParam.offset,
      }),
    getNextPageParam: (lastPage, allPages) => {
      // A page that returns nothing means we cannot make progress (e.g. a count
      // that overshoots the rows the endpoint actually serves); stop rather than
      // request the same offset forever.
      if (lastPage.contacts.length === 0) return undefined;
      const loaded = allPages.reduce((n, p) => n + p.contacts.length, 0);
      return loaded < lastPage.count
        ? { offset: loaded, limit: CONTACTS_BACKGROUND_PAGE_SIZE }
        : undefined;
    },
    staleTime: 15_000,
    // One automatic retry per page. A second failure surfaces (isError for the
    // first page, isFetchNextPageError for a later one) and stops the auto-load
    // below so the UI can offer a manual retry instead of a blank page.
    retry: 1,
  });

  // Load the remaining pages one at a time. Gated on !isError so a failed page
  // does not spin (React Query keeps status "error" while prior pages stay in
  // cache, and getNextPageParam still reports more remain). Callers that only
  // need the total (e.g. a nav badge) pass autoLoadAll:false and read `total`
  // from page 1 without sweeping the whole roster.
  const {
    hasNextPage,
    isFetchingNextPage,
    isError,
    isLoadingError,
    isFetchNextPageError,
    fetchNextPage,
  } = q;
  const loadedPages = q.data?.pages.length ?? 0;
  useEffect(() => {
    if (autoLoadAll && hasNextPage && !isFetchingNextPage && !isError) {
      void fetchNextPage();
    }
  }, [autoLoadAll, hasNextPage, isFetchingNextPage, isError, loadedPages, fetchNextPage]);

  // Stable across renders where the fetched data has not changed, so the page's
  // stats/filter memos do not thrash on every unrelated re-render.
  const { rows, dupIds } = useMemo(
    () => dedupeById((q.data?.pages ?? []).flatMap((p) => p.contacts)),
    [q.data]
  );
  const hasData = loadedPages > 0;
  const total = hasData ? q.data!.pages[0]!.count : null;
  // A complete roster once no page remains. Independent of error state: a full
  // roster stays "loaded" even if a later background refetch fails.
  const allLoaded = hasData && !hasNextPage;

  // Diagnostic: report ids the paged reader had to drop as duplicates. Under the
  // current list_contacts ordering (updated_at DESC with no unique tiebreaker),
  // offset pages can re-serve a row that another page then skips, so a non-empty
  // dupIds means the deduped roster is SHORT by that many distinct rows versus
  // the endpoint's exact `count` (that is the header-vs-"All"-tab gap). Logged on
  // the live surface (not DEV-gated) because the verbatim ids can only be
  // captured against real data; remove once the server tiebreaker lands.
  const loggedDupsRef = useRef(false);
  useEffect(() => {
    if (loggedDupsRef.current || !allLoaded || dupIds.length === 0) return;
    loggedDupsRef.current = true;
    console.warn(
      `[contacts] ${dupIds.length} duplicate id(s) dropped by dedupe - roster is short ${dupIds.length} vs endpoint count (unstable list_contacts pagination: updated_at DESC has no unique tiebreaker). Dropped ids:`,
      dupIds
    );
  }, [allLoaded, dupIds]);

  return {
    rows,
    total,
    allLoaded,
    // isPending: page 1 in flight, nothing to show yet.
    initialLoading: q.isPending,
    // isLoadingError: the query errored with NO data, i.e. page 1 itself failed.
    // Distinct from an error while data is present, so this never blanks a list
    // that has already loaded rows.
    initialError: isLoadingError,
    // Any error while some data is loaded (a later page, or a background refetch):
    // keep what loaded and offer a non-blocking retry.
    partialFailure: isError && hasData,
    retry: () => {
      if (!hasData) void q.refetch();
      else if (isFetchNextPageError) void fetchNextPage();
      else void q.refetch();
    },
  };
}

/** First occurrence wins; guards against a boundary row appearing in two pages
 *  (which would collide as a React key). Returns the dropped ids too, so the
 *  caller can report the roster-vs-count shortfall they represent. */
function dedupeById(rows: Contact[]): { rows: Contact[]; dupIds: string[] } {
  const seen = new Set<string>();
  const out: Contact[] = [];
  const dupIds: string[] = [];
  for (const c of rows) {
    if (seen.has(c.id)) {
      dupIds.push(c.id);
      continue;
    }
    seen.add(c.id);
    out.push(c);
  }
  return { rows: out, dupIds };
}

type ContactsCache = InfiniteData<ContactsPage> | undefined;

/**
 * Merge a single contact in place across every cached contacts page (any scope),
 * so a mutation reflects in the card/modal immediately instead of waiting on the
 * post-mutation roster refetch. Pages that do not hold the id are untouched.
 */
function mergeContactInCache(qc: QueryClient, id: string, fn: (c: Contact) => Contact): void {
  qc.setQueriesData<ContactsCache>({ queryKey: [KEY] }, (data) => mergeContactPages(data, id, fn));
}

/** Insert a newly created contact into every cached contacts query and bump each
 *  page's roster count (the page re-sorts, so cache placement is cosmetic). */
function insertContactInCache(qc: QueryClient, contact: Contact): void {
  qc.setQueriesData<ContactsCache>({ queryKey: [KEY] }, (data) =>
    insertContactPages(data, contact)
  );
}

/** Remove a contact by id from every cached query that holds it, decrementing the
 *  roster count only where it was present. */
function removeContactFromCache(qc: QueryClient, id: string): void {
  qc.setQueriesData<ContactsCache>({ queryKey: [KEY] }, (data) => removeContactPages(data, id));
}

/** Today as YYYY-MM-DD (local), for the optimistic contact-log stamp. */
function today(): string {
  return new Date().toISOString().slice(0, 10);
}

export function useContactMutations() {
  const adapter = useContactsAdapter();
  const { profile } = useReadyAuth();
  const qc = useQueryClient();

  // Bulk row-set change (import): actively refetch, since a merge cannot honestly
  // reconstruct dozens of server-classified/deduped inserts.
  const refetchRoster = () => qc.invalidateQueries({ queryKey: [KEY] });
  // A change already carried locally in the cache (single-row update/log, and now
  // create/delete): only MARK the roster stale (no active refetch), so revalidation
  // defers to the next natural trigger (mount / window focus / staleTime) instead
  // of actively refetching all ~20 loaded pages of the infinite query one-at-a-time.
  // That per-page sequential refetch was the multi-second settle; the local cache
  // change is authoritative, so nothing needs to block on it.
  const markStale = () => qc.invalidateQueries({ queryKey: [KEY], refetchType: "none" });

  // Create merges the returned row into cache (insert + count bump) then marks
  // stale, same non-blocking treatment as update/log - so add no longer pays the
  // whole-roster refetch (the 30-40s settle at this roster size).
  const add = useMutation({
    mutationFn: (input: ContactInput) => adapter.addContact(input, profile.id),
    onSuccess: (created) => {
      insertContactInCache(qc, created);
      markStale();
    },
  });
  // A single-row update merges its change into the cached roster on success (so
  // the card/modal reflect it at once, across every cached scope) and marks the
  // roster stale for a deferred, non-blocking revalidation.
  const update = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: ContactPatch }) =>
      adapter.updateContact(id, patch),
    onSuccess: (_result, { id, patch }) => {
      mergeContactInCache(qc, id, (c) => applyPatchToContact(c, patch));
      markStale();
    },
  });
  const log = useMutation({
    mutationFn: ({ id, method }: { id: string; method: ContactLogMethod }) =>
      adapter.logContact(id, method),
    onSuccess: (_result, { id }) => {
      const stamp = today();
      mergeContactInCache(qc, id, (c) => loggedContact(c, stamp));
      markStale();
    },
  });
  // Delete removes the row from cache (+ count adjust) then marks stale, same
  // non-blocking treatment - no whole-roster refetch.
  const remove = useMutation({
    mutationFn: (id: string) => adapter.deleteContact(id),
    onSuccess: (_result, id) => {
      removeContactFromCache(qc, id);
      markStale();
    },
  });
  const exportCsv = useMutation({
    mutationFn: (scope: ContactScope) =>
      withTimeout(
        adapter.exportCsv(scope, profile.id),
        EXPORT_TIMEOUT_MS,
        "Export timed out. Try again."
      ),
    onError: (err) => {
      console.error("Contacts export failed:", err);
    },
  });
  const previewImport = useMutation({
    mutationFn: ({ file, options }: { file: File; options: ImportOptions }) =>
      adapter.previewCsvImport(file, profile.id, options),
  });
  const confirmImport = useMutation({
    mutationFn: (preview: ImportPreview) => adapter.confirmCsvImport(preview, profile.id),
    onSuccess: refetchRoster,
  });

  return { add, update, log, remove, exportCsv, previewImport, confirmImport };
}
